import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Factory, Award, Truck, Shield, Phone, Zap, Package, Users, Battery, BatteryCharging } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PremiumChargerManufacturerIndia = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const chargerTypes = [
    { name: '15W Dual USB Chargers', protocol: 'Standard Fast', features: ['Dual Ports', 'BIS Certified', 'Compact Design'] },
    { name: '20W PD Type-C Chargers', protocol: 'Power Delivery', features: ['iPhone Fast Charging', 'Samsung Compatible', 'Universal'] },
    { name: '33W-35W VOOC Chargers', protocol: 'VOOC/Dart', features: ['OPPO/Realme Support', 'Quick Charge', 'Safe Charging'] },
    { name: '45W-65W GaN Chargers', protocol: 'Multi-Protocol', features: ['Super Fast', 'Compact GaN', 'Laptop Compatible'] }
  ];

  const protocols = [
    { name: 'Qualcomm Quick Charge', versions: '2.0, 3.0, 4.0, 5.0', brands: 'Xiaomi, Samsung, LG' },
    { name: 'USB Power Delivery (PD)', versions: 'PD 2.0, PD 3.0', brands: 'iPhone, Samsung, Universal' },
    { name: 'VOOC / Super VOOC', versions: '33W, 65W, 80W', brands: 'OPPO, Realme, OnePlus' },
    { name: 'Dash / Warp Charging', versions: '30W, 65W', brands: 'OnePlus Devices' }
  ];

  const faqs = [
    {
      q: 'Who is the best mobile charger manufacturer in India?',
      a: 'RSD Accessories Pvt Ltd is recognized as one of India\'s leading mobile charger manufacturers. Based in Noida, our BIS-certified facility produces 15,000+ chargers daily, ranging from 15W to 65W. We serve 500+ dealers across India and offer OEM manufacturing services for private label brands.'
    },
    {
      q: 'Are RSD chargers BIS certified?',
      a: 'Yes, all RSD mobile chargers are BIS (Bureau of Indian Standards) certified and comply with Legal Metrology guidelines. Our chargers undergo rigorous safety testing including over-voltage protection, over-current protection, and temperature control testing.'
    },
    {
      q: 'What wattage chargers does RSD manufacture?',
      a: 'RSD manufactures a complete range of chargers: 15W dual USB, 20W PD, 25W Samsung-compatible, 33W VOOC/Dart, 35W dual-port, 45W PD fast chargers, and 65W GaN chargers. Our latest launch is the 65W Matte Metallic Gray Edition charger.'
    },
    {
      q: 'Does RSD provide OEM charger manufacturing?',
      a: 'Yes, RSD offers comprehensive OEM and ODM services for chargers. We provide custom branding, logo printing, custom color options (including our signature matte metallic finishes), and private label packaging. MOQ starts from 5,000 units.'
    },
    {
      q: 'What fast charging protocols do RSD chargers support?',
      a: 'RSD chargers support all major protocols: Qualcomm Quick Charge (2.0-5.0), USB Power Delivery (PD 3.0), VOOC/Super VOOC, Dash/Warp Charging, Samsung Adaptive Fast Charging, and Apple 20W PD. Each charger clearly specifies supported protocols.'
    },
    {
      q: 'What safety features do RSD chargers have?',
      a: 'All RSD chargers include: Over-voltage protection (OVP), Over-current protection (OCP), Short-circuit protection, Over-temperature protection (OTP), and Auto-cutoff when fully charged. BIS certification ensures all safety standards are met.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Premium Charger Manufacturer in India | RSD Accessories - BIS Certified Factory</title>
        <meta name="description" content="RSD Accessories - India's premium mobile charger manufacturer. BIS certified, 15W-65W chargers. VOOC, QC, PD support. OEM manufacturing. Wholesale prices for distributors." />
        <meta name="keywords" content="premium charger manufacturer india, mobile charger manufacturer, bis certified charger, fast charger manufacturer, vooc charger manufacturer, oem charger manufacturer, wholesale chargers india" />
        <link rel="canonical" href="https://rsdaccessories.com/premium-charger-manufacturer-india" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-black py-20 md:py-28">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FF4D00" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <span className="inline-block bg-[#FF4D00] text-white px-4 py-1 rounded-full text-sm font-semibold mb-6">
                BIS Certified Manufacturer
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                Premium Charger Manufacturer in <span className="text-[#FF4D00]">India</span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto mb-8">
                RSD Accessories - BIS Certified Mobile Charger Factory | 15W to 65W | VOOC, QC, PD Support
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#FF4D00] hover:bg-[#E64500] text-white px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Get Wholesale Quote
                  </Button>
                </Link>
                <Link to="/shop/mobile-chargers">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                    View Charger Range
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-neutral-100 border-y border-neutral-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">15,000+</span>
                <span className="text-sm text-neutral-600">Chargers Daily</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">BIS</span>
                <span className="text-sm text-neutral-600">Certified Products</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">15W-65W</span>
                <span className="text-sm text-neutral-600">Power Range</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">100%</span>
                <span className="text-sm text-neutral-600">Safety Tested</span>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                  India's Trusted Premium Charger Manufacturer
                </h2>
                <p className="text-lg text-neutral-600 mb-6">
                  RSD Accessories is a leading BIS-certified mobile charger manufacturer in India. Our Noida-based factory produces premium quality chargers ranging from 15W standard chargers to 65W GaN super-fast chargers, supporting all major fast charging protocols.
                </p>
                <p className="text-lg text-neutral-600 mb-6">
                  Every charger undergoes 100% safety testing including over-voltage, over-current, and temperature protection verification. Our commitment to safety and quality has earned us the trust of 500+ dealers and 100+ OEM partners across India.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                    <span>BIS Certified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                    <span>Legal Metrology</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                    <span>Multi-Protocol</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                    <span>OEM Services</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <BatteryCharging className="w-8 h-8" />
                  Fast Charging Protocols
                </h3>
                <div className="space-y-4">
                  {protocols.map((protocol, idx) => (
                    <div key={idx} className="bg-white/10 rounded-lg p-4">
                      <h4 className="font-bold text-lg">{protocol.name}</h4>
                      <p className="text-white/80 text-sm">Versions: {protocol.versions}</p>
                      <p className="text-white/60 text-sm">Supports: {protocol.brands}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Charger Types */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Chargers We Manufacture
              </h2>
              <p className="text-lg text-neutral-600">BIS certified chargers for every need</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {chargerTypes.map((charger, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-neutral-100">
                  <Battery className="w-10 h-10 text-[#FF4D00] mb-4" />
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{charger.name}</h3>
                  <span className="inline-block bg-[#FF4D00]/10 text-[#FF4D00] px-3 py-1 rounded-full text-sm font-semibold mb-4">
                    {charger.protocol}
                  </span>
                  <ul className="space-y-2">
                    {charger.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-neutral-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* NEW LAUNCH Highlight */}
        <section className="py-16 bg-gradient-to-r from-neutral-900 to-neutral-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl p-8 md:p-12 border border-amber-500/30">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <span className="inline-block bg-amber-500 text-black px-4 py-1 rounded-full text-sm font-bold mb-4">
                    🔥 NEW LAUNCH
                  </span>
                  <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
                    65W Matte Metallic Gray Charger
                  </h3>
                  <p className="text-neutral-300 text-lg mb-6">
                    Our flagship premium charger with advanced safety, low heat technology, and universal compatibility. 
                    Perfect for distributors looking for premium products.
                  </p>
                  <Link to="/contact">
                    <Button className="bg-[#FF4D00] hover:bg-[#E64500]">
                      Request Bulk Quote
                    </Button>
                  </Link>
                </div>
                <div className="text-center">
                  <div className="text-6xl font-black text-[#FF4D00]">65W</div>
                  <div className="text-white">Super Fast Charging</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-neutral-600">About charger manufacturing in India</p>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">{faq.q}</h3>
                  <p className="text-neutral-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Partner with India's Premium Charger Manufacturer
            </h2>
            <p className="text-xl text-white/90 mb-8">
              BIS certified, multi-protocol support, competitive wholesale pricing
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/become-distributor">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg">
                  Become a Distributor
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PremiumChargerManufacturerIndia;
